/**
 * The assistant's unsaved work, kept in the browser so a reload does not lose it.
 *
 * A generated flow lived only in the tab until it was saved: a reload to pick up a new build, a
 * crash, or a closed tab threw away a minute of generation and the whole conversation
 * (2026-09-18). One draft per environment and flow: `new` for a flow that has never been saved,
 * the flow id afterwards, so the conversation about a saved flow also survives a reload.
 *
 * Local storage, not session storage: a closed tab is exactly the case worth surviving. Every
 * access is wrapped, because storage can be missing, full or blocked, and a draft is a
 * convenience, never something the builder may fail over.
 */

const PREFIX = 'flowgate.assistantDraft';
const VERSION = 1;
/** Well under browsers' per-origin quota; a draft past this is dropped rather than risk the quota error mid-generation. */
const MAX_BYTES = 2_000_000;

/** Per user too: two accounts sharing a browser and an environment must not see each other's draft. */
export const draftKey = (envId, scope, userId) => `${PREFIX}.${userId || 'anon'}.${envId}.${scope || 'new'}`;

export function loadDraft(envId, scope, userId) {
  if (!envId) return null;
  try {
    const raw = window.localStorage.getItem(draftKey(envId, scope, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === VERSION ? parsed : null;
  } catch {
    return null;
  }
}

export function saveDraft(envId, scope, { flowJson, messages, sessionId }, userId) {
  if (!envId) return false;
  try {
    const raw = JSON.stringify({ version: VERSION, savedAt: Date.now(), flowJson: flowJson || null, messages: messages || [], sessionId: sessionId || null });
    if (raw.length > MAX_BYTES) return false;
    window.localStorage.setItem(draftKey(envId, scope, userId), raw);
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(envId, scope, userId) {
  if (!envId) return;
  try {
    window.localStorage.removeItem(draftKey(envId, scope, userId));
  } catch {
    // Nothing to do: if storage is unavailable there was no draft to clear.
  }
}
